"use client"

/**
 * The files of one note, comment or past visit as one quiet chip — the owner's pick "C" (2026-10-18):
 * up to three round previews and "2 photos · 1 video"; a click opens the viewer at the first file.
 */
import { Loader2, Maximize2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import type { AttachmentItem } from "./types"

interface AttachmentChipProps {
  items: AttachmentItem[]
  onOpen: (index: number) => void
  className?: string
}

export function AttachmentChip({ items, onOpen, className }: AttachmentChipProps) {
  const { t } = useTranslation()
  if (!items.length) return null
  const photos = items.filter((i) => i.kind === "photo").length
  const videos = items.length - photos
  const processing = items.some((i) => i.processing)
  const label = [
    // one key per number form, as elsewhere in the journal: other languages word "{{count}}" count-neutrally
    photos === 1 ? t("attachments.one_photo", "1 photo") : photos ? t("attachments.n_photos", "{{count}} photos", { count: photos }) : "",
    videos === 1 ? t("attachments.one_video", "1 video") : videos ? t("attachments.n_videos", "{{count}} videos", { count: videos }) : "",
  ].filter(Boolean).join(" · ")

  return (
    <button
      type="button"
      onClick={() => onOpen(0)}
      className={cn(
        "inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3",
        "text-xs text-gray-600 cursor-zoom-in transition-colors hover:border-gray-300 hover:text-gray-900",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400",
        className,
      )}
      aria-label={t("attachments.open", "Open photos and videos: {{label}}", { label })}
    >
      <span className="flex shrink-0">
        {items.slice(0, 3).map((it, i) => (
          <img
            key={it.id}
            src={it.thumb}
            alt=""
            loading="lazy"
            className={cn("h-6 w-6 rounded-full border-2 border-white bg-gray-100 object-cover", i > 0 && "-ml-2.5")}
          />
        ))}
      </span>
      <span className="truncate">{label}</span>
      {processing && <Loader2 className="h-3 w-3 shrink-0 animate-spin text-gray-400" aria-hidden />}
      <Maximize2 className="h-3 w-3 shrink-0 text-gray-400" aria-hidden />
    </button>
  )
}
